import { FC } from "react";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { StepLayoutContainer } from "components/Layout/StepLayoutContainer";
import CustomTable from "components/Table/CustomTable";
import { REQUIREMENT } from "helpers/constants";
import { ParsedRequirementRow } from "../parseRequirementsCsv";
import {
  ProcessingSettings,
  RequirementImportType,
  ResolvedCompetencyLink,
} from "../types";
import { CompetencyLinksList } from "../components/CompetencyLinksList";

type Props = {
  requirementType: RequirementImportType;
  requirements: ParsedRequirementRow[];
  resolveCompetencyLinks: (names: string[]) => ResolvedCompetencyLink[];
  processing: ProcessingSettings;
  loading: boolean;
  loadingProgress: { current: number; total: number };
  onBack: () => void;
  onSubmit: () => void;
};

export const ReviewStep: FC<Props> = ({
  requirementType,
  requirements,
  resolveCompetencyLinks,
  processing,
  loading,
  loadingProgress,
  onBack,
  onSubmit,
}) => {
  const isEpaImport = requirementType === REQUIREMENT.REQUIREMENT_EPA;
  const isIterImport = requirementType === REQUIREMENT.REQUIREMENT_ITER;
  const linksCompetenciesFromCsv = isEpaImport || isIterImport;
  const rowTypeLabel = isEpaImport ? "EPA" : isIterImport ? "ITER" : "row";
  const totalRubrics = requirements.reduce(
    (sum, row) => sum + row.rubrics.length,
    0,
  );
  const totalCompetencies = requirements.reduce(
    (sum, row) => sum + row.competencies.length,
    0,
  );
  const missingCompetencies = linksCompetenciesFromCsv
    ? requirements.reduce(
        (sum, row) =>
          sum +
          resolveCompetencyLinks(row.competencies).filter((link) => !link.found)
            .length,
        0,
      )
    : 0;

  return (
    <StepLayoutContainer
      title="Review and import"
      description="Confirm the requirements to import and submit."
      onClickBack={loading ? undefined : onBack}
      onClickNext={loading ? undefined : onSubmit}
      nextButtonText={loading ? "Importing..." : "Import"}
      renderActionButtons={
        loading ? (
          <Box>
            <Typography variant="body2" align="center" mb={1}>
              Importing{" "}
              {loadingProgress.total > 0
                ? ~~((loadingProgress.current / loadingProgress.total) * 100)
                : 0}
              %
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                loadingProgress.total > 0
                  ? (loadingProgress.current / loadingProgress.total) * 100
                  : 0
              }
            />
          </Box>
        ) : undefined
      }
    >
      <Box mt={2}>
        <Typography variant="subtitle1" gutterBottom>
          {requirements.length} {rowTypeLabel}(s)
          {linksCompetenciesFromCsv
            ? ` · ${totalCompetencies} competency link(s)${
                missingCompetencies > 0
                  ? ` (${missingCompetencies} not found)`
                  : ""
              }`
            : ""}{" "}
          {totalRubrics > 0 ? `· ${totalRubrics} rubric milestone(s)` : ""}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {!isIterImport && (
            <>
              Task time: {processing.hour}:
              {String(processing.minutes).padStart(2, "0")} · Min attempts:{" "}
              {processing.min_performance} · Min score: {processing.min_score}
            </>
          )}
          {processing.tags.length > 0
            ? `${isIterImport ? "" : " · "}${processing.tags.length} tag(s)`
            : ""}
          {processing.resources.length > 0
            ? `${processing.tags.length > 0 || !isIterImport ? " · " : ""}${processing.resources.length} resource(s)`
            : ""}
        </Typography>
        <CustomTable
          entities={requirements}
          columns={[
            {
              id: "name",
              label: isEpaImport ? "EPA Name" : isIterImport ? "ITER Name" : "Name",
              value: "name",
            },
            {
              id: "description",
              label: "Description",
              value: (row: ParsedRequirementRow) =>
                row.description?.length > 64
                  ? `${row.description.slice(0, 64)}...`
                  : row.description,
            },
            ...(linksCompetenciesFromCsv
              ? [
                  {
                    id: "competencies",
                    label: "Competencies",
                    value: (row: ParsedRequirementRow) =>
                      resolveCompetencyLinks(row.competencies)
                        .map((link) => link.matchedName ?? link.csvName)
                        .join(", ") || "—",
                  },
                ]
              : []),
            ...(requirementType === REQUIREMENT.REQUIREMENT_COMPETENCY
              ? [
                  {
                    id: "rubrics",
                    label: "Rubrics",
                    value: (row: ParsedRequirementRow) => row.rubrics.length,
                  },
                ]
              : []),
          ]}
          handleActionClick={() => {}}
          fieldToFilter={(row: ParsedRequirementRow, filterText: string) =>
            row.name.toLowerCase().includes(filterText.toLowerCase())
          }
          isSelectable={false}
          extraActionItems={[]}
          idColumn="row_key"
        />

        {linksCompetenciesFromCsv && (
          <Box mt={3} pb={3}>
            <Typography variant="subtitle1" gutterBottom>
              Competencies to link
            </Typography>
            <Stack spacing={2}>
              {requirements.map((row) => (
                <Box key={row.row_key}>
                  <Typography variant="subtitle2" gutterBottom>
                    {row.name}
                  </Typography>
                  <CompetencyLinksList
                    links={resolveCompetencyLinks(row.competencies)}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </StepLayoutContainer>
  );
};
